import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { gopherService, TokenNewsData, TwitterTweet } from '../services/gopherService';

interface TwitterNewsProps {
  token: string;
  onClose?: () => void;
}

const TwitterNews: React.FC<TwitterNewsProps> = ({ token, onClose }) => {
  const [newsData, setNewsData] = useState<TokenNewsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      fetchTokenNews();
    }
  }, [token]);

  const fetchTokenNews = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let data: TokenNewsData;
      
      if (gopherService.isConfigured()) {
        data = await gopherService.getTokenNews(token);
      } else {
        // Use demo data when API key is not configured
        data = await gopherService.getDemoTokenNews(token);
      }
      
      setNewsData(data);
    } catch (err) {
      setError('Failed to fetch token news');
      console.error('Error fetching token news:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (sentiment: 'bullish' | 'bearish' | 'neutral') => {
    switch (sentiment) {
      case 'bullish': return 'text-green-400';
      case 'bearish': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const getSentimentIcon = (sentiment: 'bullish' | 'bearish' | 'neutral') => {
    switch (sentiment) {
      case 'bullish': return '🚀';
      case 'bearish': return '📉';
      default: return '➡️';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const tweetTime = new Date(dateString);
    const diffMs = now.getTime() - tweetTime.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffHours > 0) return `${diffHours}h ago`;
    return `${diffMinutes}m ago`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading) {
    return (
      <Card className="p-6 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30">
        <div className="flex items-center justify-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-400"></div>
          <span className="text-green-400">Fetching Twitter news for ${token}...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-gradient-to-br from-red-500/10 to-[#0a0a1a] border-red-500/30">
        <div className="text-center">
          <div className="text-red-400 mb-2">❌ Error</div>
          <div className="text-red-400/80 text-sm mb-4">{error}</div>
          <Button 
            onClick={fetchTokenNews}
            className="bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30"
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  if (!newsData) return null;

  const { sentiment, tweets } = newsData;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-green-400">
          📰 Twitter News: ${newsData.token}
        </h2>
        {onClose && (
          <Button
            onClick={onClose}
            variant="outline"
            className="border-green-500/40 text-green-400/80 hover:bg-green-500/10"
          >
            ✕ Close
          </Button>
        )}
      </div>

      {/* Sentiment Overview */}
      <Card className="p-6 bg-gradient-to-br from-green-500/10 to-[#0a0a1a] border-green-500/30">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Overall Sentiment */}
          <div className="text-center">
            <div className="text-4xl mb-2">
              {getSentimentIcon(sentiment.overall_sentiment)}
            </div>
            <div className={`text-2xl font-bold ${getSentimentColor(sentiment.overall_sentiment)}`}>
              {sentiment.overall_sentiment.toUpperCase()}
            </div>
            <div className="text-green-400/70 text-sm">Overall Sentiment</div>
            <div className="mt-2">
              <div className="text-green-400 font-medium">
                Score: {sentiment.sentiment_score.toFixed(2)}
              </div>
              <div className="text-green-400/70 text-xs">
                Confidence: {(sentiment.confidence * 100).toFixed(0)}%
              </div>
            </div>
          </div>

          {/* Sentiment Breakdown */}
          <div>
            <h3 className="text-green-400 font-medium mb-3">Sentiment Breakdown</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-green-400">🚀 Bullish</span>
                <span className="text-green-400 font-bold">{sentiment.bullish_tweets}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-red-400">📉 Bearish</span>
                <span className="text-red-400 font-bold">{sentiment.bearish_tweets}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-yellow-400">➡️ Neutral</span>
                <span className="text-yellow-400 font-bold">{sentiment.neutral_tweets}</span>
              </div>
              <div className="border-t border-green-500/30 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-green-400/70">Total Tweets</span>
                  <span className="text-green-400 font-bold">{sentiment.total_tweets}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-6 p-4 bg-black/40 rounded-lg border border-green-500/20">
          <h4 className="text-green-400 font-medium mb-2">📊 Analysis Summary</h4>
          <p className="text-green-400/80 text-sm">{sentiment.summary}</p>
          
          {sentiment.key_themes.length > 0 && (
            <div className="mt-3">
              <span className="text-green-400/70 text-xs">Key Themes: </span>
              <div className="flex flex-wrap gap-2 mt-1">
                {sentiment.key_themes.map((theme, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full"
                  >
                    {theme}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Recent Tweets */}
      <div>
        <h3 className="text-lg font-bold text-green-400 mb-4">🐦 Recent Tweets</h3>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {tweets.slice(0, 10).map((tweet) => (
            <Card
              key={tweet.id}
              className={`p-4 border transition-all hover:border-green-500/50 ${
                tweet.sentiment === 'bullish'
                  ? 'bg-gradient-to-r from-green-500/10 to-[#0a0a1a] border-green-500/30'
                  : tweet.sentiment === 'bearish'
                  ? 'bg-gradient-to-r from-red-500/10 to-[#0a0a1a] border-red-500/30'
                  : 'bg-gradient-to-r from-yellow-500/10 to-[#0a0a1a] border-yellow-500/30'
              }`}
            >
              {/* Tweet Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-green-400 font-medium">
                    @{tweet.author.username}
                  </span>
                  {tweet.author.verified && (
                    <span className="text-blue-400">✓</span>
                  )}
                  <span className="text-green-400/50 text-xs">
                    {formatNumber(tweet.author.followers_count)} followers
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm ${getSentimentColor(tweet.sentiment || 'neutral')}`}>
                    {getSentimentIcon(tweet.sentiment || 'neutral')}
                  </span>
                  <span className="text-green-400/50 text-xs">
                    {formatTimeAgo(tweet.created_at)}
                  </span>
                </div>
              </div>

              {/* Tweet Content */}
              <p className="text-green-400/90 text-sm mb-3 leading-relaxed">
                {tweet.text}
              </p>

              {/* Tweet Metrics */}
              <div className="flex items-center space-x-4 text-xs text-green-400/60">
                <span>❤️ {formatNumber(tweet.public_metrics.like_count)}</span>
                <span>🔄 {formatNumber(tweet.public_metrics.retweet_count)}</span>
                <span>💬 {formatNumber(tweet.public_metrics.reply_count)}</span>
                {tweet.relevance_score && (
                  <span className="ml-auto">
                    Relevance: {(tweet.relevance_score * 100).toFixed(0)}%
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Refresh Button */}
      <div className="text-center">
        <Button
          onClick={fetchTokenNews}
          className="bg-green-600/30 border border-green-500/40 text-green-300 hover:bg-green-500/20"
        >
          🔄 Refresh News
        </Button>
        <div className="text-green-400/50 text-xs mt-2">
          Last updated: {new Date(newsData.last_updated).toLocaleTimeString()}
        </div>
        {!gopherService.isConfigured() && (
          <div className="text-yellow-400/70 text-xs mt-1">
            ⚠️ Demo mode - Configure GOPHER_API_KEY for live data
          </div>
        )}
      </div>
    </div>
  );
};

export default TwitterNews;
